import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { callRpc } from '@/lib/supabaseTyped';
import { logger } from '@/lib/logger';
import { observability } from '@/lib/observability';
import { refreshSessionWithBackoff } from '@/lib/authRefresh';
import { rememberAuthMethod, forgetAuthMethod } from '@/lib/lastAuthMethod';
import type { User, Session } from '@supabase/supabase-js';

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  /**
   * True iff the user has verified their email (Supabase sets
   * `email_confirmed_at` on click-through). Gated features like coop
   * sharing should require this.
   */
  isEmailVerified: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signUp: (email: string, password: string) => Promise<{ error: string | null }>;
  /** Send a password-reset link to the email; the link points at /reset-password. */
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  /** Used by /reset-password after the user clicks the link to set a new password. */
  updatePassword: (newPassword: string) => Promise<{ error: string | null }>;
  /** OAuth sign-in (Google / Microsoft / GitHub). Redirects to provider. */
  signInWithOAuth: (provider: 'google' | 'azure' | 'github') => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  /** Sista ban-state + reason från senaste blockerade sign-in. Undefined om ej aktuellt. */
  lastBanBlock: { state: string | null; reason: string | null } | null;
  clearLastBanBlock: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastBanBlock, setLastBanBlock] = useState<{ state: string | null; reason: string | null } | null>(null);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      setIsLoading(false);
      // #89: vendor-neutral breadcrumb + user-id ident. ALDRIG email/token —
      // bara user-id (eller null vid sign-out). Eventnamn är Supabase-events
      // ('SIGNED_IN', 'SIGNED_OUT', 'TOKEN_REFRESHED', …).
      observability.addBreadcrumb({
        category: 'auth',
        message: `auth.${event.toLowerCase()}`,
        data: { hasSession: !!s },
        level: 'info',
      });
      observability.identifyUser(s?.user?.id ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Pro-aktiv token-refresh — long-lived tabs på flaky network kan
  // missa Supabase's interna auto-refresh-cycle och hamna med en
  // expired access-token. Vi sätter en timer som refreshar 60s före
  // `expires_at` och signar ut om refresh failar 3 gånger i rad.
  useEffect(() => {
    if (!session?.expires_at) return;
    const expiresAtMs = session.expires_at * 1000;
    // Refresh 60s before expiry, klämt i [5s, 25min] för att undvika
    // både omedelbara loopar och timer-IDs som aldrig triggar.
    const delayMs = Math.max(5_000, Math.min(expiresAtMs - Date.now() - 60_000, 25 * 60_000));

    let cancelled = false;
    const timer = setTimeout(async () => {
      if (cancelled) return;
      const result = await refreshSessionWithBackoff();
      if (!cancelled && result === 'failed') {
        logger.warn('[auth] proactive refresh failed after retries — signing out');
        try { await supabase.auth.signOut(); } catch { /* ignored */ }
      }
    }, delayMs);

    return () => { cancelled = true; clearTimeout(timer); };
  }, [session?.expires_at]);

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };

    // #55: kom ihåg metoden för nästa "Senast"-hint i AuthDialog.
    rememberAuthMethod('email');

    // Post-login ban check via security definer RPC (avoids RLS issues)
    try {
      type BanRow = { is_banned?: boolean; ban_reason?: string | null; state?: string | null };
      const { data: banData } = await callRpc<BanRow | BanRow[]>('check_my_ban_status');
      const row: BanRow | null = Array.isArray(banData) ? (banData[0] ?? null) : banData;
      if (row?.is_banned) {
        const reason = row.ban_reason ?? '';
        setLastBanBlock({ state: row.state ?? null, reason });
        await supabase.auth.signOut();
        return { error: `BANNED:${reason}` };
      }
    } catch (err) {
      // If the check fails, allow sign-in (don't lock everyone out on a
      // transient error) — but log it so support can diagnose the case
      // where a user's ban isn't enforced because the RPC is broken.
      logger.warn('Ban-check RPC failed; allowing sign-in', {
        message: err instanceof Error ? err.message : String(err),
      });
    }

    return { error: null };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    // Match the admin-invite-user Edge Function's Zod min of 8 chars so
    // admin-created accounts and self-service sign-ups share one floor.
    if (password.length < 8) {
      return { error: 'Password must be at least 8 characters' };
    }
    // emailRedirectTo styr vart bekräftelse-länken landar. Utan denna
    // faller Supabase tillbaka på Site URL (= lovable preview-domänen) →
    // användaren tvingas logga in i Lovable. Med window.location.origin
    // landar de tillbaka på samma domän de signade upp från (engicoro.se,
    // www.engicoro.se, eller preview).
    const emailRedirectTo = typeof window !== 'undefined'
      ? `${window.location.origin}/flow-weaver`
      : undefined;
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo },
    });
    return { error: error?.message ?? null };
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    // The link lands on /reset-password where updatePassword() is called.
    const redirectTo = typeof window !== 'undefined'
      ? `${window.location.origin}/reset-password`
      : undefined;
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo });
    return { error: error?.message ?? null };
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (newPassword.length < 8) {
      return { error: 'Password must be at least 8 characters' };
    }
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error: error?.message ?? null };
  }, []);

  const signInWithOAuth = useCallback(async (provider: 'google' | 'azure' | 'github') => {
    const redirectTo = typeof window !== 'undefined'
      ? `${window.location.origin}/flow-weaver`
      : undefined;
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo },
    });
    return { error: error?.message ?? null };
  }, []);

  const signOut = useCallback(async () => {
    // #55: rensa method-hint så ny user på samma device inte ser fel "Senast".
    forgetAuthMethod();
    await supabase.auth.signOut();
  }, []);

  const clearLastBanBlock = useCallback(() => setLastBanBlock(null), []);

  return (
    <AuthContext.Provider value={{
      user,
      session,
      isLoading,
      isAuthenticated: !!session,
      // `email_confirmed_at` is set by Supabase on click-through. Absent
      // for OAuth users whose provider already verified the email — those
      // reach the app with `email_confirmed_at` populated by the adapter.
      isEmailVerified: !!(user?.email_confirmed_at || user?.confirmed_at),
      signIn,
      signUp,
      resetPassword,
      updatePassword,
      signInWithOAuth,
      signOut,
      lastBanBlock,
      clearLastBanBlock,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
